import {
  ControlledDateOnlyPicker,
  ControlledNumberField,
  ControlledSelect,
  ErrorAlert,
  useDollarAdornment,
} from '@if/mui'
import { Collapse, Stack } from '@mui/material'
import { useComputeCalendarAction } from '@src/api/loan/loan-api'
import FormDialog from '@src/components/Common/Dialog/FormDialog'
import { useUnmountableDialog } from '@src/services/dialog'
import { EOperationStatus } from '@src/types/constants'
import { ECalendarSimulationAction, SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { FormEventHandler, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { getStatusThatNeedsTransactionDate, useChangePaymentStatusResolver } from './LoanCalendarPaymentStatus-schema'
import { useAvailablePaymentStatuses } from './LoanCalendarPaymentStatus-selectors'
import NsfStatusSelect from './NsfStatusSelect'

interface LoanCalendarPaymentStatusDialogProps {
  paymentIds: Set<string>
  initialSignedSimulation: SignedCalendarSimulation
  onConfirm: (data: SignedCalendarSimulation) => void
  onClose: () => void
  isLockedByOther?: boolean
}

const LoanCalendarPaymentStatusDialog = ({
  paymentIds,
  initialSignedSimulation,
  onConfirm,
  onClose,
  isLockedByOther = false,
}: LoanCalendarPaymentStatusDialogProps) => {
  const { t } = useTranslation()
  const {
    mutateAsync: computeAction,
    error,
    isPending,
  } = useComputeCalendarAction(
    initialSignedSimulation.simulation.fullLoan.loan.id,
    initialSignedSimulation.simulation.fullLoan.loan.financingProgramId,
  )

  const resolver = useChangePaymentStatusResolver()
  const originalSimulation = initialSignedSimulation.simulation
  const { dialogProps, closeDialog } = useUnmountableDialog(onClose, isPending)
  const firstOperation = useMemo(() => {
    const firstOperationInformation = originalSimulation.operationInformations.find((oi) =>
      paymentIds.has(oi.operation.id),
    )
    return firstOperationInformation!.operation
  }, [originalSimulation, paymentIds])

  const [newSimulationWithSignature, setNewSimulationWithSignature] =
    useState<SignedCalendarSimulation>(initialSignedSimulation)
  const currentStatus = firstOperation.status
  const dollarAdornment = useDollarAdornment()

  const {
    control,
    watch,
    formState: { isValid, isValidating, isSubmitting },
    subscribe,
    setValue,
  } = useForm({
    resolver,
    mode: 'onChange',
    context: { simulation: originalSimulation, currentStatus: currentStatus },
    disabled: isPending,
  })

  const { newStatus, transactionDate, nsfFee, nsfStatus } = watch()

  const availablePaymentStatusesOption = useAvailablePaymentStatuses(currentStatus)
  const transactionDateDisabled = useMemo(() => !getStatusThatNeedsTransactionDate().has(newStatus), [newStatus])
  // Not using the handleSubmit to avoid unnecessary re-validation
  const onSubmitForm: FormEventHandler = (e) => {
    e.preventDefault()
    if (newSimulationWithSignature && !isPending) {
      onConfirm(newSimulationWithSignature)
      closeDialog()
    }
  }

  useEffect(() => {
    if (isValid && !isPending && !isValidating && !isSubmitting) {
      // Chaining simulations for each payment to be changed
      void computeAction({
        ...initialSignedSimulation,
        action: {
          actionType: ECalendarSimulationAction.ChangePaymentStatus,
          newStatus,
          nsfFee: nsfFee ?? 0,
          transactionDate,
          paymentIds: Array.from(paymentIds),
          nsfStatus: nsfStatus ?? undefined,
        },
      }).then((computedSimulation) => {
        setNewSimulationWithSignature(computedSimulation)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newStatus, nsfFee, transactionDate, nsfStatus, isValid, isValidating, isSubmitting])

  useEffect(() => {
    // Passively reset dependent fields when newStatus changes
    const unsubscribe = subscribe({
      name: ['newStatus'],
      formState: {
        values: true,
      },
      callback: ({ values: { newStatus } }) => {
        // Reset dependent fields
        if (!getStatusThatNeedsTransactionDate().has(newStatus)) {
          setValue('transactionDate', null as never)
          setValue('nsfFee', null as never)
          setValue('nsfStatus', null as never)
        }
      },
    })
    return () => {
      unsubscribe()
    }
  }, [setValue, subscribe])

  return (
    <FormDialog
      {...dialogProps}
      onConfirm={onSubmitForm}
      title={t('loan.payment.changeStatusPayment.title')}
      disabled={isPending || !isValid || isLockedByOther}
      loading={isPending}
      showLoadingBar
      disableContentPaddingTop
    >
      {error && <ErrorAlert error={error} />}
      <Stack spacing={2} marginTop={2}>
        <Stack direction="row" spacing={1} marginTop={2}>
          <ControlledSelect
            name="newStatus"
            control={control}
            options={availablePaymentStatusesOption}
            label={t('loan.payment.changeStatusPayment.newStatus')}
            disableEmptyOption
            disabled={isLockedByOther}
          />
          <ControlledDateOnlyPicker
            name="transactionDate"
            control={control}
            label={t('loan.payment.changeStatusPayment.transactionDate')}
            disabled={transactionDateDisabled || isLockedByOther}
          />
        </Stack>
        <Collapse in={newStatus === EOperationStatus.Failed}>
          <Stack direction="row" spacing={1}>
            <ControlledNumberField
              control={control}
              name="nsfFee"
              slotProps={{ input: dollarAdornment }}
              label={t('loan.payment.changeStatusPayment.nsfFee')}
              disableDecimals
              disabled={newStatus !== EOperationStatus.Failed || isLockedByOther}
            />
            <NsfStatusSelect
              control={control}
              name="nsfStatus"
              disabled={newStatus !== EOperationStatus.Failed || isLockedByOther}
            />
          </Stack>
        </Collapse>
      </Stack>
    </FormDialog>
  )
}

export default LoanCalendarPaymentStatusDialog
